import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { getReservationById, patchReservation, updateReservation } from "api";
import {
  cancelReservationAlert,
  successfulCancelAlert,
  successfulReserveAlert,
  successfulUpdatedAlert,
} from "swal2";
import FancyLine from "images/FancyLine.png";
import { convertTo12Hour, getFirstWord } from "functions";
import { resBookSvg } from "svg";
import TableFinder from "./TableFinder";
import FoundTable from "./FoundTable";
import { fadeIn } from "animations";
import { motion, AnimatePresence } from "motion/react";
import { dateToString } from "dateUtils";

const SHELL_SPRING = { type: "spring", stiffness: 140, damping: 26, mass: 1.1 };
const CARD_SPRING = { type: "spring", stiffness: 160, damping: 28, mass: 1.0 };

const SWAP = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: { duration: 0.24, ease: [0.22, 1, 0.36, 1] },
};

const SHELL_W_VIEW = 660;
const SHELL_W_EDIT = 820;
const CARD_W_VIEW = 560;
const CARD_W_EDIT = 720;

function Reservation() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isSuccess = searchParams.has("success");
  const navigate = useNavigate();
  const [reservation, setReservation] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [isModify, setModify] = useState(false);
  const [envelopePlayed, setEnvelopePlayed] = useState(false);

  const [editTable, setEditTable] = useState(null);
  const [isEditing, setIsEditing] = useState(true);
  const mountRef = useRef(null);

  useEffect(() => {
    const node = mountRef.current;
    if (!node) return;
    node.style.setProperty("--rx", "0deg");
    node.style.setProperty("--ry", "0deg");
    node.style.setProperty("--gx", "50%");
    node.style.setProperty("--gy", "50%");
  }, [isModify]);

  useEffect(() => {
    if (isSuccess) {
      successfulReserveAlert();
    }
  }, [isSuccess]);

  useEffect(() => {
    const abortController = new AbortController();
    const signal = abortController.signal;

    const loadReservation = async () => {
      try {
        const responseData = await getReservationById(id, signal);
        if (responseData) {
          setReservation(responseData);
        } else {
          setNotFound(true);
        }
      } catch (error) {
        if (error?.name === "CanceledError" || error?.name === "AbortError")
          return;
        console.error(error);
        setNotFound(true);
      }
    };

    if (!reservation) loadReservation();

    return () => {
      abortController.abort();
    };
  }, [id, reservation]);

  const isLargeParty = reservation?.numGuests >= 7;

  const enterModify = () => {
    if (isLargeParty) return;
    setEditTable({ ...reservation });
    setIsEditing(true);
    setModify(true);
  };

  const exitModify = () => {
    setModify(false);
    setIsEditing(true);
    setEditTable(null);
  };

  const returnToEdit = () => {
    setEditTable((prev) => ({ ...prev, time: "" }));
    setIsEditing(true);
  };

  const updateRes = async (newTable) => {
    const updatedRes = {
      ...reservation,
      ...newTable,
    };

    try {
      const response = await updateReservation(id, updatedRes);
      if (response.status === 200) {
        successfulUpdatedAlert();
        setReservation(null);
        exitModify();
      }
    } catch (error) {
      console.error(error);
    }
  };

  async function cancelRes() {
    try {
      const obj = await cancelReservationAlert();
      if (obj.isConfirmed) {
        const response = await patchReservation(id, "cancel", "Customer");
        if (response.status === 200) {
          const promise = await successfulCancelAlert();
          if (promise) navigate("/");
        }
      }
    } catch (error) {
      console.error(error);
    }
  }

  const handleTilt = (e) => {
    if (isModify) return;
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    const rotateY = ((x - cx) / cx) * 1.75;
    const rotateX = -((y - cy) / cy) * 1.75;
    card.style.setProperty("--rx", `${rotateX}deg`);
    card.style.setProperty("--ry", `${rotateY}deg`);
    card.style.setProperty("--gx", `${(x / rect.width) * 100}%`);
    card.style.setProperty("--gy", `${(y / rect.height) * 100}%`);
  };

  const resetTilt = (e) => {
    const card = e.currentTarget;
    card.style.setProperty("--rx", `0deg`);
    card.style.setProperty("--ry", `0deg`);
    card.style.setProperty("--gx", `50%`);
    card.style.setProperty("--gy", `50%`);
  };

  const renderStatusCard = (title, message, name) => (
    <motion.div {...fadeIn} className="review-container res-container">
      <div className="review-box res-box">
        <div className="res-card-mount">
          <div className="res-header-block res-header-block-inmount">
            <div className="res-header-stack">
              <div className="menu-section-header res-header-text">
                {title}
              </div>
              <img
                className="fancy-line review-line"
                src={FancyLine}
                alt=""
              />
            </div>
          </div>
          <div className="res-card">
            <span
              className="res-card-tuck res-card-tuck-tl"
              aria-hidden="true"
            />
            <span
              className="res-card-tuck res-card-tuck-tr"
              aria-hidden="true"
            />
            <span
              className="res-card-tuck res-card-tuck-bl"
              aria-hidden="true"
            />
            <span
              className="res-card-tuck res-card-tuck-br"
              aria-hidden="true"
            />
            <div className="res-card-greeting">{message}</div>
            {name && <div className="res-card-name">{name}</div>}
            <div
              style={{
                paddingTop: 24,
                paddingBottom: 8,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <button
                className="clean-button"
                onClick={() => navigate("/reserve")}
              >
                {resBookSvg()} Reserve
              </button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );

  if (notFound)
    return renderStatusCard(
      "This reservation has been cancelled",
      "We hope to serve you another time"
    );
  if (!reservation) return <div className="empty" />;
  if (reservation.state === "cancel")
    return renderStatusCard(
      "This reservation has been cancelled",
      "We hope to serve you another time",
      reservation.name
    );
  if (reservation.state === "arrived")
    return (
      <div className="review-container res-container">
        <div className="review-box">
          <div className="menu-section-header">
            We hope to see you again soon
          </div>
          <img className="fancy-line review-line" src={FancyLine} alt="" />
          <div
            style={{
              paddingTop: "0px",
              paddingBottom: "40px",
              color: "#444444",
              textAlign: "left",
            }}
          >
            It was a pleasure to serve you, {getFirstWord(reservation.name)}
          </div>

          <div
            style={{
              paddingTop: "20px",
              paddingBottom: "20px",
              color: "#444444",
            }}
          >
            To make a new reservation, please click below
          </div>
          <button className="clean-button" onClick={() => navigate("/reserve")}>
            {resBookSvg()} Reserve
          </button>
        </div>
      </div>
    );

  return (
    <motion.div {...fadeIn} className="review-container res-container">
      <div className="review-box res-box res-box-fluid">
        <motion.div
          ref={mountRef}
          animate={{ width: isModify ? SHELL_W_EDIT : SHELL_W_VIEW }}
          transition={SHELL_SPRING}
          className={`res-card-mount res-card-mount-fluid ${
            isModify ? "res-card-mount-edit" : "res-card-tilt"
          }`}
          onMouseMove={!isModify ? handleTilt : undefined}
          onMouseLeave={!isModify ? resetTilt : undefined}
        >
          <motion.div
            layout="position"
            className="res-header-block res-header-block-inmount"
          >
            <motion.div layout="position" className="res-header-stack">
              <motion.div
                layout
                layoutId="res-header-text"
                transition={CARD_SPRING}
                className="menu-section-header res-header-text"
              >
                {isModify ? "Modify Your Reservation" : "Your Reservation"}
              </motion.div>
              <motion.img
                layout
                layoutId="res-fancy-line"
                transition={CARD_SPRING}
                className="fancy-line review-line"
                src={FancyLine}
                alt=""
              />
              <AnimatePresence initial={false}>
                {isModify && (
                  <motion.div
                    key="subheader"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
                    className="res-subheader res-subheader-inmount"
                    style={{ overflow: "hidden" }}
                  >
                    Subject to table availability
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </motion.div>

          <motion.div
            animate={{ maxWidth: isModify ? CARD_W_EDIT : CARD_W_VIEW }}
            transition={CARD_SPRING}
            className={`res-card res-card-fluid${
              isModify ? " res-card-edit" : ""
            }${envelopePlayed ? "" : " res-card-popin"}`}
            onAnimationEnd={() => setEnvelopePlayed(true)}
          >
            <span
              className="res-card-tuck res-card-tuck-tl"
              aria-hidden="true"
            />
            <span
              className="res-card-tuck res-card-tuck-tr"
              aria-hidden="true"
            />
            <span
              className="res-card-tuck res-card-tuck-bl"
              aria-hidden="true"
            />
            <span
              className="res-card-tuck res-card-tuck-br"
              aria-hidden="true"
            />
            <motion.div
              layout
              layoutId="res-greeting"
              transition={CARD_SPRING}
              className="res-card-greeting"
            >
              We look forward to serving you
            </motion.div>
            <motion.div
              layout
              layoutId="res-name"
              transition={CARD_SPRING}
              className={`res-card-name${
                isModify ? " res-card-name-edit" : ""
              }`}
            >
              {reservation.name}
            </motion.div>

            <motion.div
              layout
              layoutId="res-divider"
              transition={CARD_SPRING}
              className="res-card-divider"
              aria-hidden="true"
            >
              <span className="res-card-divider-line" />
              <span className="res-card-divider-ornament">✦</span>
              <span className="res-card-divider-line" />
            </motion.div>

            <motion.div
              layout
              transition={CARD_SPRING}
              className="res-card-body"
            >
              <AnimatePresence mode="popLayout" initial={false}>
                {isModify ? (
                  <motion.div key="body-edit" layout {...SWAP}>
                    <AnimatePresence mode="popLayout" initial={false}>
                      {isEditing ? (
                        <motion.div key="finder" layout {...SWAP}>
                          <TableFinder
                            table={editTable}
                            setTable={setEditTable}
                            editing={true}
                            setEditing={setIsEditing}
                          />
                        </motion.div>
                      ) : (
                        <motion.div key="found" layout {...SWAP}>
                          <FoundTable
                            table={editTable}
                            setEditing={returnToEdit}
                            inRes={true}
                            onSave={updateRes}
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ) : (
                  <motion.div
                    key="body-view"
                    layout
                    {...SWAP}
                    className="res-card-details"
                  >
                    <div className="res-detail">
                      <div className="res-detail-label">Party</div>
                      <div className="res-detail-value">
                        {reservation.numGuests}{" "}
                        {reservation.numGuests === 1 ? "Guest" : "Guests"}
                      </div>
                    </div>
                    <div className="res-detail res-detail-center">
                      <div className="res-detail-label">Date</div>
                      <div className="res-detail-value">
                        {dateToString(new Date(reservation.date))}
                      </div>
                    </div>
                    <div className="res-detail">
                      <div className="res-detail-label">Time</div>
                      <div className="res-detail-value">
                        {convertTo12Hour(reservation.time)}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            <motion.div
              layout
              layoutId="res-note"
              transition={CARD_SPRING}
              className="res-card-note"
            >
              Your reservation will be held for 10 minutes
            </motion.div>
          </motion.div>
        </motion.div>

        <motion.div layout="position" className="res-buttons">
          <AnimatePresence mode="wait" initial={false}>
            {isModify ? (
              <motion.div
                key="btn-edit"
                {...SWAP}
                className="res-buttons-row"
              >
                <button
                  className="submit-button res-cancel-btn"
                  onClick={exitModify}
                >
                  Cancel Editing
                </button>
              </motion.div>
            ) : (
              <motion.div
                key="btn-view"
                {...SWAP}
                className="res-buttons-stack"
              >
                {isLargeParty && (
                  <div className="res-large-party-note">
                    To modify this reservation please call us at
                    <br />
                    <a className="res-large-party-phone" href="tel:847-332-2330">
                      847-332-2330
                    </a>
                  </div>
                )}
                <div className="res-buttons-row">
                  {!isLargeParty && (
                    <button
                      className="submit-button modify-btn"
                      onClick={enterModify}
                    >
                      Modify
                    </button>
                  )}
                  <button
                    className="submit-button res-cancel-btn"
                    onClick={() => cancelRes()}
                  >
                    Cancel Reservation
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </motion.div>
  );
}

export default Reservation;
